import React, { createContext, useCallback, useEffect, useState, useMemo, useRef } from 'react'
import { useParams, useNavigate } from 'react-router-dom'
import { toast } from 'react-toastify'

// Internal Service & Class imports
import { useWakeLock } from '../hooks/useWakelock'
import { usePip } from '../hooks/usePip'
import api from '../service/api'
import { getTransformedMeetingType } from '../utils/meeting-utilities'

// Stores
import { useMeetingStore } from '../store/meeting.store'
import { useUserStore } from '../store/user.store'
import { useVirtualboardStore } from '../store/virtualboard.store'
import { useOnlineSessionsSocketStore } from '../store/onlinesessionsocket.store'

import MeetingCoordinator from '../service/MeetingCoordinator'
import { mainClient, shareClient } from '../service/MeetingClient'
import { onCameraChanged, onMicrophoneChanged } from 'agora-rtc-sdk-ng/esm'

const MeetingActionsContext = createContext()

export const MeetingActionsContextProvider = ( { children } ) => {
    const { meeting_id } = useParams()
    const navigate = useNavigate()

    const [webinarPipWindow, setWebinarPipWindow] = useState( null )
    const [joining, setJoining] = useState( false )

    // safeRejoinMeeting is called from several places (connection-state-change,
    // ReconnectHandler, visibility-change handling). Retry/backoff state lives
    // here so it's centralized regardless of who triggers the rejoin.
    const [isRejoining, setIsRejoining] = useState( false )
    const [rejoinAttempt, setRejoinAttempt] = useState( 0 )
    const [rejoinGaveUp, setRejoinGaveUp] = useState( false )

    const isRejoiningRef = useRef( false )
    const rejoinAttemptRef = useRef( 0 )
    const rejoinTimerRef = useRef( null )

    const REJOIN_MAX_RETRIES = 8
    const REJOIN_BASE_DELAY = 2000


    // Feature Hooks
    const { requestWakeLock, releaseWakeLock } = useWakeLock()
    const { enterPipMode, closePip } = usePip( setWebinarPipWindow )

    // Stores
    const MeetingStore = useMeetingStore()
    const UserStore = useUserStore()
    const OnlineSessionSocketStore = useOnlineSessionsSocketStore()
    const VirtualboardStore = useVirtualboardStore()

    // Memoize the Coordinator so it's only created once
    const coordinator = useMemo( () => new MeetingCoordinator( {
        MeetingStore, UserStore, OnlineSessionSocketStore, VirtualboardStore
    } ), [MeetingStore, UserStore, OnlineSessionSocketStore, VirtualboardStore] )

    // --- Actions ---

    const joinChannel = useCallback( async () => {
        try {
            setJoining( true )
            MeetingStore.resetOldJoinedData()

            const access = JSON.parse( sessionStorage.getItem( 'meetingAccessToken' ) || '{}' )
            if ( access?.meeting_id !== meeting_id ) {
                return MeetingStore.setCurrentMeetingAccessDetails( { unauth: true } )
            }

            await OnlineSessionSocketStore.connect( meeting_id, MeetingStore.getLocalUser?.user_id )
            await requestWakeLock()
            await coordinator.orchestrateJoin( meeting_id )

        } catch ( err ) {
            console.error( "Join Process Failed:", err )
            toast( err.message || "Failed to join meeting" )
        } finally {
            setJoining( false )
        }
    }, [meeting_id, coordinator, requestWakeLock, OnlineSessionSocketStore, MeetingStore] )

    const safeRejoinMeeting = useCallback( async () => {
        // Prevent overlapping rejoin attempts from the multiple listeners that can trigger this
        if ( isRejoiningRef.current ) return
        isRejoiningRef.current = true
        setIsRejoining( true )
        setRejoinGaveUp( false )

        try {
            // eslint-disable-next-line no-constant-condition
            while ( true ) {
                try {
                    toast.info( "Reconnecting..." )
                    await mainClient.leave()
                    await OnlineSessionSocketStore.disconnect()

                    const { data: { data: freshTokens } } = await api.onlineMeetings.getMeetingAccessToken( meeting_id, UserStore.getUser )
                    sessionStorage.setItem( "meetingAccessToken", JSON.stringify( { meeting_id, ...freshTokens } ) )

                    await OnlineSessionSocketStore.connect( meeting_id, UserStore.getUser.user_id )
                    await coordinator.orchestrateJoin( meeting_id, true, true )

                    rejoinAttemptRef.current = 0
                    setRejoinAttempt( 0 )
                    return
                } catch ( err ) {
                    console.error( "Rejoin Failed:", err )

                    if ( rejoinAttemptRef.current >= REJOIN_MAX_RETRIES ) {
                        setRejoinGaveUp( true )
                        throw err
                    }

                    const delay = REJOIN_BASE_DELAY * Math.pow( 2, rejoinAttemptRef.current )
                    rejoinAttemptRef.current += 1
                    setRejoinAttempt( rejoinAttemptRef.current )

                    await new Promise( ( resolve ) => { rejoinTimerRef.current = setTimeout( resolve, delay ) } )
                }
            }
        } finally {
            isRejoiningRef.current = false
            setIsRejoining( false )
        }
    }, [meeting_id, UserStore, coordinator, OnlineSessionSocketStore] )

    useEffect( () => () => {
        if ( rejoinTimerRef.current ) clearTimeout( rejoinTimerRef.current )
    }, [] )

    const leaveChannel = useCallback( async ( reason = "manual-leave" ) => {
        await mainClient.leave()
        await shareClient.leave()
        await releaseWakeLock()
        closePip()

        if ( coordinator.recorderInterval ) clearInterval( coordinator.recorderInterval )
        if ( MeetingStore.getMeetingState.isRecording )
            await coordinator.toggleRecording()
        await OnlineSessionSocketStore.emit( 'meeting:leave', { reason } )
        await OnlineSessionSocketStore.disconnect()

        const type = getTransformedMeetingType( MeetingStore.getCurrentMeeting.meeting_type )
        navigate( `/${type}/${MeetingStore.getCurrentMeeting.id}/feedback` )

        MeetingStore.setMeetingState( { isJoined: false, isSharing: false } )
    }, [MeetingStore, OnlineSessionSocketStore, navigate, releaseWakeLock, closePip, coordinator] )


    const stopScreenShare = useCallback( async () => {
        await shareClient.leave()
        const stopResponse = await OnlineSessionSocketStore.emit( 'meeting:stop-sharing' )
        console.log( stopResponse )

        MeetingStore.setMeetingState( { isSharing: false, sharingState: { surface: null, screen: false, audio: false, audioEnabled: false } } )
        MeetingStore.removeRemoteUser( `${MeetingStore.getLocalUser.user_id}-sharing` )
        console.log( `${MeetingStore.getLocalUser.user_id}-sharing removed` )

    }, [OnlineSessionSocketStore, MeetingStore] )

    const toggleScreenShare = useCallback( async () => {
        if ( MeetingStore.getMeetingState.isSharing ) {
            await stopScreenShare()
        } else {
            try {
                const tracks = await shareClient.createScreenTracks( null, stopScreenShare )
                const surface = shareClient.getSharingSurface()

                const access = JSON.parse( sessionStorage.getItem( 'meetingAccessToken' ) || '{}' )
                await shareClient.join( meeting_id, access.agoraChannelShareToken, `${MeetingStore.getLocalUser.user_id}-sharing`, MeetingStore.getLocalUser )
                await shareClient.publish()

                if ( surface && surface !== "browser" ) {
                    enterPipMode( setWebinarPipWindow, () => MeetingStore.setMeetingState( { pipWindowEnabled: false } ) )
                }

                MeetingStore.setMeetingState( { isSharing: true, sharingState: { surface, screen: true, audio: !!tracks.audio, audioEnabled: true } } )
                OnlineSessionSocketStore.emit( 'meeting:join', { type: "share-client", room_id: meeting_id, user_id: `${MeetingStore.getLocalUser.user_id}-sharing`, sharing: true, meeting: MeetingStore.getCurrentMeeting } )
            } catch ( err ) {
                console.error( err )
            }
        }
    }, [meeting_id, stopScreenShare, MeetingStore, OnlineSessionSocketStore, enterPipMode] )

    // --- Global Event Listeners ---
    useEffect( () => {
        const client = mainClient.client

        // --- Remote User Handlers ---

        const handleUserPublished = async ( remoteUser, mediaType ) => {
            try {
                await client.subscribe( remoteUser, mediaType )
                // Agora SDK user object is mapped to your remote user list
                MeetingStore.addOrUpdateRemoteUsers( [remoteUser], 'agora-server', "added" )

                if ( remoteUser.uid.toString().includes( 'sharing' ) ) {
                    MeetingStore.addOrRemoveSharingUsers( remoteUser, "add" )
                }
            } catch ( err ) {
                console.error( "Subscription Error:", err )
            }
        }

        const handleUserUnpublished = ( remoteUser, mediaType ) => {
            if ( remoteUser.uid.toString().includes( 'sharing' ) ) {
                if ( mediaType === 'video' ) {
                    MeetingStore.addOrRemoveSharingUsers( remoteUser, "removed" )
                    MeetingStore.removeRemoteUser( remoteUser.uid, "Unpublished video sharing" )
                }
            } else {
                // Update the user state in the store (e.g., to show they turned off cam)
                MeetingStore.addOrUpdateRemoteUsers( [remoteUser], 'agora-server', "added" )
            }
        }

        const handleUserJoined = ( remoteUser ) => {
            MeetingStore.addOrUpdateRemoteUsers( [remoteUser], 'agora-server', "added" )
        }

        const handleUserLeft = ( remoteUser ) => {
            MeetingStore.removeRemoteUser( remoteUser.uid, "Handle User Left AGORA" )

            if ( remoteUser.uid.toString().includes( 'sharing' ) ) {
                MeetingStore.addOrRemoveSharingUsers( remoteUser, "removed" )
            }

            const { activePresenter } = MeetingStore.getMeetingState
            if ( activePresenter && activePresenter.user_id === remoteUser.uid ) {
                MeetingStore.setMeetingState( { activePresenter: null } )
            }
        }

        // --- Connectivity Handlers ---

        const handleConnectionChange = async ( cur, prev, reason ) => {
            if ( cur === "RECONNECTING" ) {
                MeetingStore.setMeetingState( { connectionLost: true } )
            }

            if ( cur === "CONNECTED" ) {
                MeetingStore.setMeetingState( { connectionLost: false } )
                // If we were re-connecting, sync the socket state
                if ( prev === "RECONNECTING" ) {
                    try {
                        await OnlineSessionSocketStore.emit( "meeting:sync-users", { room_id: meeting_id } )
                    } catch ( err ) {
                        console.error( "Socket Sync Failed:", err )
                    }
                }
            }

            if ( cur === "DISCONNECTED" && reason !== "LEAVE" ) {
                MeetingStore.setMeetingState( { connectionLost: false } )
                safeRejoinMeeting().catch( () => {} )
            }
        }

        // Network-level signal used only to drive the "connection lost" UI state.
        // Actual rejoin attempts are triggered solely by handleConnectionChange
        // (connection-state-change) to avoid duplicate, overlapping rejoin calls.
        const handleNetworkChange = ( cur ) => {
            if ( cur === "DISCONNECTED" || cur === "RECONNECTING" ) {
                MeetingStore.setMeetingState( { connectionLost: true } )
            } else if ( cur === "CONNECTED" ) {
                MeetingStore.setMeetingState( { connectionLost: false } )
            }
        }

        const handleNetworkQuality = ( quality ) => {
            // quality.uplinkNetworkQuality and downlinkNetworkQuality
            MeetingStore.setNetworkStats( {
                uplink: quality.uplinkNetworkQuality,
                downlink: quality.downlinkNetworkQuality,
                lastUpdated: Date.now()
            } )
        }

        /**
         * Monitor specific SDK exceptions like 'LOST_CONNECTION' or 'LOW_BANDWIDTH'
         */
        const handleException = ( event ) => {
            console.warn( `Agora Exception: ${event.code} - ${event.msg}` )
            if ( event.code === 2001 ) { // Example: Low Bandwidth warning
                toast.warning( "Network connection unstable. Quality may decrease." )
            }

            MeetingStore.setNetworkException( {
                code: event.code,
                msg: event.msg,
                timestamp: Date.now()
            } )
        }

        const handleSessionStats = ( stats ) => {
            MeetingStore.setSessionStats( {
                cpuUsage: stats.CPUUsage,
                duration: stats.Duration,
                userCount: stats.UserCount,
                outgoingBitrate: stats.SendBitrate,
                incomingBitrate: stats.RecvBitrate,
                rtt: stats.RTT // Round Trip Time
            } )
        }

        // --- Hardware Change Logs ---
        onCameraChanged( ( device ) => console.log( "Camera Changed:", device ) )
        onMicrophoneChanged( ( device ) => console.log( "Microphone Changed:", device ) )

        const statsInterval = setInterval( () => {
            if ( client.connectionState === "CONNECTED" ) {
                const stats = client.getRTCStats()
                handleSessionStats( stats )
            }
        }, 2000 )

        // --- Listener Binding ---
        client.on( "network-quality", handleNetworkQuality )
        client.on( "exception", handleException )
        client.on( "user-published", handleUserPublished )
        client.on( "user-unpublished", handleUserUnpublished )
        client.on( "user-joined", handleUserJoined )
        client.on( "user-left", handleUserLeft )
        client.on( "connection-state-change", handleConnectionChange )
        client.on( "network-state-change", handleNetworkChange )

        return () => {
            client.off( "user-published", handleUserPublished )
            client.off( "user-unpublished", handleUserUnpublished )
            client.off( "user-joined", handleUserJoined )
            client.off( "user-left", handleUserLeft )
            client.off( "connection-state-change", handleConnectionChange )
            client.off( "network-state-change", handleNetworkChange )
            client.off( "network-quality", handleNetworkQuality )
            client.off( "exception", handleException )
            clearInterval( statsInterval )
        }
    }, [MeetingStore, meeting_id, OnlineSessionSocketStore, safeRejoinMeeting] )

    return (
        <MeetingActionsContext.Provider value={{
            turnOnCamera: ( val ) => coordinator.toggleCamera( val ),
            turnOnMicrophone: ( val ) => coordinator.toggleMicrophone( val ),
            publishVideo: () => coordinator.publishVideo(),
            publishAudio: () => coordinator.publishAudio(),
            stopScreenShare,
            toggleScreenShare,
            toggleScreenRecording: () => coordinator.toggleRecording(),
            toggleScreenShareAudio: ( val ) => coordinator.toggleScreenShareAudio( val ),
            replaceSharingTrack: () => coordinator.replaceSharingTrack( stopScreenShare ),
            joinChannel,
            leaveChannel,
            releaseWakeLock,
            requestWakeLock,
            safeRejoinMeeting,
            webinarPipWindow,
            joining,
            connectionLost: MeetingStore.getMeetingState.connectionLost,
            isRejoining,
            rejoinAttempt,
            rejoinMaxRetries: REJOIN_MAX_RETRIES,
            rejoinGaveUp
        }}>
            {children}
        </MeetingActionsContext.Provider>
    )
}

export const useMeetingActionsContext = () => React.useContext( MeetingActionsContext )
export default MeetingActionsContextProvider